import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import {
  replenishActiveFromReserve, posPackSize, posReserveUnits, posActiveUnits,
  posUnitsFromEntry, describePosUnits, posUnitNoun, posPackNoun,
} from '../utils/posUtil';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import PosSelect from './PosSelect';
import { useConfig } from '../context/ConfigContext';
import { usePosSession } from '../context/PosSessionContext';
import '../css/Pos.css';

/**
 * Carry stock out of the storeroom and onto the shelf.
 *
 * ASKS FOR PACKS AND PIECES SEPARATELY, and adds them together. "Ten bottles,
 * not a whole box" is the ordinary case at a counter — someone is topping up a
 * fridge, not unloading a delivery — so taking a few loose pieces has to be as
 * easy as taking a box, and taking one of each has to be possible in one go.
 *
 * The pack row is hidden entirely for a product with no pack size. A "boxes"
 * field on grip tape sold by the piece is a field that can only ever be wrong.
 *
 * Nothing is written until Confirm: replenishActiveFromReserve moves both
 * counts in a single transaction, so a refused move leaves the storeroom
 * exactly as it found it.
 *
 * TWO WAYS IN, one dialog. From the Reserve tab a row names the product, so
 * it arrives fixed. From the Active tab there is no row to start from — you
 * are standing at the fridge deciding what to top up — so it arrives as a list
 * to choose from instead. Everything below the choice is identical, which is
 * the point: one place where the arithmetic and the transaction live.
 *
 * @param {object} props
 * @param {object} [props.product] - the product being replenished, when the
 *   caller already knows which one
 * @param {object[]} [props.products] - choose from these instead. Products
 *   with nothing in reserve are dropped: there is nothing to carry out.
 * @param {() => void} props.onClose
 * @param {(result: {moved: number, active: number, reserve: number}) => void} [props.onDone]
 * @returns {JSX.Element}
 */
function PosReplenishModal({ product, products, onClose, onDone }) {
  const { clientId } = useConfig();
  const { staffEmail } = usePosSession();
  const [packs, setPacks] = useState('');
  const [units, setUnits] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Only products with something in the storeroom are offered. A dropdown
  // listing things you cannot take is a list of dead ends.
  const choices = useMemo(
    () => (products || []).filter((p) => posReserveUnits(p) > 0),
    [products],
  );
  const picking = Boolean(products);
  const [chosenId, setChosenId] = useState(() => (products ? (choices[0]?.id || '') : ''));

  const target = picking
    ? (choices.find((p) => p.id === chosenId) || choices[0] || null)
    : product;

  const packSize = posPackSize(target);
  const reserve = posReserveUnits(target);
  const active = posActiveUnits(target);

  const moving = posUnitsFromEntry({ packs, units }, packSize);
  const enough = moving <= reserve;
  const canMove = Boolean(target) && moving >= 1 && enough;

  const handleConfirm = async () => {
    if (saving || !canMove) return;
    setSaving(true);
    setError('');
    try {
      const result = await replenishActiveFromReserve(clientId, target, { packs, units }, staffEmail);
      if (onDone) onDone(result);
      onClose();
    } catch (err) {
      setError(describeWriteError(err, {
        what: 'this stock move',
        path: `posTenants/${clientId}/products`,
      }));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    // No backdrop click-to-close: this holds unsaved input (CLAUDE.md >
    // Behavior). The × and Cancel are the ways out.
    <div className="pos-modal-overlay">
      <div className="pos-modal pos-modal-narrow" role="dialog" aria-modal="true" aria-label="Replenish active stock">
        <header className="pos-modal-head">
          <h3>Replenish active stock</h3>
          <button type="button" className="pos-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="pos-modal-body">
          {picking ? (
            choices.length === 0 ? (
              <p className="pos-empty-line">
                Nothing in reserve yet. Register a product on the Reserve Stocks tab, or
                record a delivery against one, and it will appear here.
              </p>
            ) : (
              <div className="pos-replenish-pick">
                <PosSelect
                  label="Product"
                  options={choices.map((p) => ({ key: p.id, label: p.name }))}
                  value={target?.id}
                  onChange={(id) => {
                    setChosenId(id);
                    // The entry is cleared with the product: "2 boxes" of one
                    // thing is not 2 boxes of the next, whose box may hold a
                    // different number.
                    setPacks('');
                    setUnits('');
                    setError('');
                  }}
                />
              </div>
            )
          ) : (
            <p className="pos-replenish-lead">{target?.name}</p>
          )}

          {target && (
          <>

          {/* Where it is coming from and where it is going, before anything is
              typed — the two figures the decision is actually made on. */}
          <div className="pos-replenish-now">
            <div>
              <span>In reserve</span>
              <strong>{describePosUnits(reserve, product)}</strong>
            </div>
            <div>
              <span>On the shelf</span>
              <strong>{active} {posUnitNoun(product, active)}</strong>
            </div>
          </div>

          <div className="pos-replenish-entry">
            {packSize > 0 && (
              <label className="pos-field">
                <span>Whole {posPackNoun(product, 2)}</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={packs}
                  onChange={(e) => setPacks(e.target.value)}
                  disabled={saving}
                  placeholder="0"
                />
                <span className="pos-theme-hint">
                  {packSize} {posUnitNoun(product, packSize)} each
                </span>
              </label>
            )}

            <label className="pos-field">
              <span>Loose {posUnitNoun(product, 2)}</span>
              <input
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={units}
                onChange={(e) => setUnits(e.target.value)}
                disabled={saving}
                placeholder="0"
              />
              <span className="pos-theme-hint">
                {packSize > 0
                  ? `Singles not in a ${posPackNoun(product, 1)} — fill in both to move both`
                  : 'How many to move'}
              </span>
            </label>
          </div>

          {/* What Confirm will DO, worked through. The arithmetic is the part
              people get wrong — two boxes of 24 is not an obvious 48 when you
              are also taking five loose. */}
          {moving > 0 && (
            <div className={`pos-replenish-preview${enough ? '' : ' short'}`}>
              {enough ? (
                <>
                  <p>
                    Moving <strong>{describePosUnits(moving, product)}</strong> to the shelf.
                  </p>
                  <p className="pos-replenish-after">
                    Shelf becomes <strong>{active + moving} {posUnitNoun(product, active + moving)}</strong>
                    {' · '}
                    reserve becomes <strong>{describePosUnits(reserve - moving, product)}</strong>
                  </p>
                </>
              ) : (
                <p>
                  That is more than the storeroom holds — there {reserve === 1 ? 'is' : 'are'} only{' '}
                  <strong>{describePosUnits(reserve, product)}</strong> in reserve.
                </p>
              )}
            </div>
          )}

          </>
          )}

          {error && <p className="pos-modal-error">{error}</p>}
        </div>

        <footer className="pos-modal-foot">
          <span className="pos-foot-spacer" />
          <button type="button" className="pos-btn pos-btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="pos-btn pos-btn-primary" onClick={handleConfirm} disabled={saving || !canMove}>
            {saving ? 'Moving…' : 'Confirm'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default PosReplenishModal;
